import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../home/components/Header';
import Footer from '../home/components/Footer';
import ProductNav from '../../components/feature/ProductNav';
import { BookingCalendar } from '../home/components/BookingCalendar';
import StarRatingDisplay from '../../components/feature/StarRatingDisplay';
import MobileProductCTABar from '../../components/feature/MobileProductCTABar';

const CANONICAL = 'https://hoosierlifeinsurance.com/mortgage-life-insurance-indianapolis';
const SITE_URL = 'https://hoosierlifeinsurance.com';

/* ─── Mortgage Payoff Calculator ─────────────────────────────────────────── */

interface PayoffResult {
  loanBalance: number;
  termYears: number;
  monthlyPayment: number;
  termLifeMonthly: number;
  mortgageLifeMonthly: number;
  termLifeSavings: number;
  recommendation: string;
  recommendationDetail: string;
}

function calculatePayoff(
  balance: number,
  termYears: number,
  age: number,
  healthClass: 'preferred' | 'standard' | 'substandard'
): PayoffResult {
  const rate = 0.065;
  const monthlyRate = rate / 12;
  const n = termYears * 12;
  const monthlyPayment = balance * (monthlyRate * Math.pow(1 + monthlyRate, n)) / (Math.pow(1 + monthlyRate, n) - 1);

  // Term life rate per $1000 of coverage (simplified)
  const termRateTable: Record<string, Record<string, number>> = {
    preferred: { '30': 0.08, '35': 0.10, '40': 0.14, '45': 0.20, '50': 0.30, '55': 0.48 },
    standard: { '30': 0.11, '35': 0.14, '40': 0.19, '45': 0.28, '50': 0.42, '55': 0.68 },
    substandard: { '30': 0.16, '35': 0.21, '40': 0.29, '45': 0.42, '50': 0.63, '55': 1.02 },
  };

  const ageKey = String(Math.min(55, Math.max(30, Math.round(age / 5) * 5)));
  const ratePerThousand = termRateTable[healthClass][ageKey] ?? 0.20;
  const termLifeMonthly = (balance / 1000) * ratePerThousand;

  // Mortgage life insurance is typically 15-25% more expensive and decreasing benefit
  const mortgageLifeMonthly = termLifeMonthly * 1.22;

  const termLifeSavings = (mortgageLifeMonthly - termLifeMonthly) * termYears * 12;

  let recommendation = '';
  let recommendationDetail = '';

  if (healthClass === 'preferred' || healthClass === 'standard') {
    recommendation = 'Level Term Life Insurance';
    recommendationDetail = `For your profile, a ${termYears}-year level term policy at $${balance.toLocaleString()} coverage saves you approximately $${Math.round(termLifeSavings).toLocaleString()} over the life of the policy vs. mortgage life insurance — with the added benefit that your beneficiary receives the full death benefit regardless of remaining mortgage balance.`;
  } else {
    recommendation = 'Simplified Issue Term Life';
    recommendationDetail = `With your health profile, a simplified issue term policy may be your best option. Coverage up to $500,000 with no medical exam — approved in 24–48 hours. We\'ll compare all available carriers to find your best rate.`;
  }

  return { loanBalance: balance, termYears, monthlyPayment, termLifeMonthly, mortgageLifeMonthly, termLifeSavings, recommendation, recommendationDetail };
}

/* ─── FAQs ───────────────────────────────────────────────────────────────── */

const FAQS = [
  {
    q: 'What is mortgage life insurance in Indianapolis?',
    a: 'Mortgage life insurance in Indianapolis (also called mortgage protection insurance or mortgage payoff insurance) is a life insurance policy specifically designed to pay off your mortgage if you die before it\'s paid off. The death benefit equals your remaining mortgage balance and decreases over time as you pay down your loan. If you die, the insurance company pays your lender directly — your family keeps the home free and clear. Mortgage life insurance is available in Indianapolis from carriers like SBLI, Protective Life, and Banner Life. However, most Indianapolis financial advisors recommend level term life insurance instead — it costs less, pays your beneficiary directly (not the lender), and the death benefit doesn\'t decrease.',
  },
  {
    q: 'How much does mortgage life insurance cost in Indianapolis?',
    a: 'Mortgage life insurance in Indianapolis typically costs $50–$150/month for a $250,000 mortgage, depending on your age and health. Compare this to a level term life insurance policy for the same $250,000 coverage amount: a healthy 40-year-old Indianapolis homeowner pays approximately $22–$28/month for a 20-year term policy — 40–60% less than mortgage life insurance. The reason mortgage life insurance costs more: the death benefit decreases as you pay down your mortgage, but your premium stays the same. You\'re paying the same amount for less and less coverage over time. An independent agent can show you the exact cost comparison for your specific mortgage balance, age, and health.',
  },
  {
    q: 'Is mortgage life insurance worth it in Indianapolis?',
    a: 'For most Indianapolis homeowners, mortgage life insurance is NOT the best value. Here\'s why: (1) Decreasing benefit — your death benefit shrinks as you pay down your mortgage, but your premium stays the same. (2) Lender is the beneficiary — the insurance company pays your lender directly, not your family. Your family has no flexibility to use the money for other needs. (3) Higher cost — mortgage life insurance typically costs 20–40% more than an equivalent level term policy. (4) No portability — if you refinance or sell your home, the policy may not transfer. The better alternative for most Indianapolis homeowners: a level term life insurance policy for the full mortgage amount. Your family receives the full death benefit regardless of remaining mortgage balance, and they can use it however they need — pay off the mortgage, cover living expenses, fund college, etc.',
  },
  {
    q: 'What is the difference between mortgage life insurance and term life insurance in Indianapolis?',
    a: 'The key differences between mortgage life insurance and term life insurance in Indianapolis: Death Benefit: Mortgage life insurance has a decreasing death benefit (shrinks as you pay down your mortgage). Term life insurance has a level death benefit (stays the same for the entire term). Beneficiary: Mortgage life insurance pays your lender directly. Term life insurance pays your chosen beneficiary — your spouse, children, or estate. Cost: Mortgage life insurance typically costs 20–40% more than equivalent term life coverage. Flexibility: Term life insurance gives your family complete flexibility — they can pay off the mortgage, cover living expenses, fund college, or invest the money. Mortgage life insurance only pays off the mortgage. Portability: Term life insurance stays with you regardless of refinancing or moving. Mortgage life insurance may not transfer. For most Indianapolis homeowners, level term life insurance is the better choice.',
  },
  {
    q: 'Can I get mortgage life insurance with bad credit in Indianapolis?',
    a: 'Yes — mortgage life insurance in Indianapolis does not require a credit check. Eligibility is based on your age and health, not your credit score. Simplified issue mortgage protection policies are available with no medical exam — just answer health questions online or by phone. Guaranteed issue policies are available for ages 50–85 with no health questions and no credit check. However, if you have bad credit, you may also want to consider whether a level term life insurance policy is a better fit — term life also doesn\'t require a credit check, and it provides more flexible coverage for your family.',
  },
  {
    q: 'How much mortgage life insurance do I need in Indianapolis?',
    a: 'The amount of mortgage life insurance you need in Indianapolis equals your current outstanding mortgage balance. For example, if you owe $285,000 on your Indianapolis home, you need $285,000 of mortgage life insurance. However, most Indianapolis financial advisors recommend getting a level term life insurance policy for your full mortgage balance plus additional income replacement — typically 10–12x your annual income. This ensures your family can pay off the mortgage AND maintain their standard of living if you die. A $500,000 level term policy for a 40-year-old Indianapolis homeowner costs approximately $28–$35/month — often less than a $285,000 mortgage life insurance policy.',
  },
  {
    q: 'What is the best mortgage life insurance in Indianapolis?',
    a: 'The best mortgage life insurance in Indianapolis depends on your age, health, and coverage needs. For healthy Indianapolis homeowners under age 60: Level term life insurance from Banner Life, Protective Life, or Pacific Life is typically the best value — lower cost, level death benefit, and your family is the beneficiary. For Indianapolis homeowners with health conditions: Simplified issue term life from Transamerica or North American Company — no medical exam, approved in 24–48 hours. For Indianapolis seniors ages 50–80: Simplified issue whole life or final expense insurance from Mutual of Omaha or North American Company. An independent agent compares all available carriers simultaneously to find your best rate and coverage for your specific mortgage payoff situation.',
  },
  {
    q: 'Does mortgage life insurance cover the full mortgage in Indianapolis?',
    a: 'Mortgage life insurance in Indianapolis covers your remaining mortgage balance at the time of death — not the original loan amount. The death benefit decreases over time as you pay down your mortgage. For example: if you take out a $300,000 mortgage life insurance policy today and die 10 years later when your balance is $220,000, the insurance company pays $220,000 to your lender. This is why many Indianapolis homeowners prefer level term life insurance — the death benefit stays at $300,000 for the entire term, regardless of your remaining mortgage balance. Your family receives the full $300,000 and can decide how to use it.',
  },
  {
    q: 'Can I get mortgage life insurance without a medical exam in Indianapolis?',
    a: 'Yes — mortgage life insurance without a medical exam is available in Indianapolis. Simplified issue mortgage protection policies require only health questions (no blood draw, no doctor visit) and approve in 24–48 hours. Coverage up to $500,000 is available with no exam. Guaranteed issue policies for ages 50–85 require no health questions and approve instantly — coverage up to $25,000. For Indianapolis homeowners who want no-exam coverage for their full mortgage balance, simplified issue term life insurance from Transamerica or Banner Life\'s accelerated underwriting program (up to $1M, no exam) are excellent options.',
  },
  {
    q: 'What happens to mortgage life insurance if I refinance in Indianapolis?',
    a: 'If you refinance your Indianapolis home, your mortgage life insurance policy may not automatically update to reflect your new loan balance or terms. Some policies are tied to the original lender and may not transfer to a new lender. If you refinance to a lower rate or extend your term, you may need to purchase a new mortgage life insurance policy — potentially at a higher premium due to your older age. This is another reason many Indianapolis homeowners prefer level term life insurance: it\'s completely portable. If you refinance, sell your home, or move, your term life policy stays with you and your family is always protected for the full death benefit amount.',
  },
  {
    q: 'How do I apply for mortgage life insurance in Indianapolis?',
    a: 'Applying for mortgage life insurance in Indianapolis is simple. Step 1: Call Hoosier Life Insurance at (317) 648-2983 or book a free 15-minute consultation. Step 2: We\'ll review your mortgage balance, remaining term, age, and health to determine whether mortgage life insurance or level term life insurance is the better fit for your situation. Step 3: We compare all available carriers simultaneously — including no-exam options if you prefer. Step 4: We submit your application and guide you through the approval process. Step 5: Coverage begins — your Indianapolis home is protected. Most Indianapolis homeowners are approved within 24–72 hours for no-exam policies, or 2–4 weeks for fully underwritten policies.',
  },
];

/* ─── Comparison Table Data ──────────────────────────────────────────────── */

const COMPARISON = [
  { feature: 'Death Benefit', mortgageLife: 'Decreasing — shrinks as you pay down mortgage', termLife: 'Level — stays the same for entire term', winner: 'term' },
  { feature: 'Beneficiary', mortgageLife: 'Lender — pays mortgage directly', termLife: 'Your family — they decide how to use it', winner: 'term' },
  { feature: 'Cost', mortgageLife: 'Higher — 20–40% more than equivalent term', termLife: 'Lower — most affordable option', winner: 'term' },
  { feature: 'Flexibility', mortgageLife: 'None — only pays off mortgage', termLife: 'Full — mortgage, living expenses, college, etc.', winner: 'term' },
  { feature: 'Portability', mortgageLife: 'May not transfer if you refinance or move', termLife: 'Fully portable — stays with you always', winner: 'term' },
  { feature: 'No-Exam Option', mortgageLife: 'Available (simplified issue)', termLife: 'Available (up to $1M with accelerated UW)', winner: 'tie' },
  { feature: 'Approval Speed', mortgageLife: '24–72 hours (simplified issue)', termLife: '24–48 hours (accelerated UW)', winner: 'tie' },
  { feature: 'Best For', mortgageLife: 'Homeowners who can\'t qualify for term life', termLife: 'Most Indianapolis homeowners', winner: 'term' },
];

/* ─── Testimonials ───────────────────────────────────────────────────────── */

const TESTIMONIALS = [
  {
    name: 'James & Carla W.',
    location: 'Lawrence, Indianapolis',
    quote: 'We were sold mortgage life insurance by our lender at closing. Benjamin showed us we were paying $87/month for decreasing coverage — and could get $350K of level term for $31/month. Switched immediately. Saved over $600/year.',
    stars: 5,
    savings: '$672/year',
    coverage: '$350K level term',
  },
  {
    name: 'Michael T.',
    location: 'Decatur Township, Indianapolis',
    quote: 'Just bought my first home in Indianapolis. Benjamin walked me through mortgage protection vs. term life. Got a 30-year $300K term policy for $28/month. My family is protected and I\'m not overpaying.',
    stars: 5,
    savings: 'First-time buyer',
    coverage: '$300K 30-yr term',
  },
  {
    name: 'Sandra & Robert H.',
    location: 'Warren Township, Indianapolis',
    quote: 'We have some health issues and were worried we couldn\'t get coverage. Benjamin found us a simplified issue policy — no exam, approved in 36 hours. Our $240K mortgage is fully protected.',
    stars: 5,
    savings: 'No exam approved',
    coverage: '$240K simplified issue',
  },
  {
    name: 'Patricia M.',
    location: 'Pike Township, Indianapolis',
    quote: 'Refinanced last year and my old mortgage life policy didn\'t transfer. Benjamin got me a new level term policy that\'s portable — no matter what happens with my mortgage, my family is covered.',
    stars: 5,
    savings: 'Portable coverage',
    coverage: '$275K level term',
  },
];

/* ─── Who Needs Mortgage Life ────────────────────────────────────────────── */

const WHO_NEEDS = [
  { icon: 'ri-home-4-line', title: 'New Homeowners', desc: 'Just closed on your Indianapolis home — protect your mortgage from day one.' },
  { icon: 'ri-parent-line', title: 'Young Families', desc: 'Ensure your spouse and children can stay in the home if you die unexpectedly.' },
  { icon: 'ri-refresh-line', title: 'Recent Refinancers', desc: 'Just refinanced? Make sure your coverage matches your new loan balance and term.' },
  { icon: 'ri-heart-pulse-line', title: 'Health Conditions', desc: 'Simplified issue options available — no medical exam, approved in 24–48 hours.' },
  { icon: 'ri-briefcase-line', title: 'Self-Employed', desc: 'No employer life insurance? Protect your family\'s biggest asset independently.' },
  { icon: 'ri-user-heart-line', title: 'Single Income Households', desc: 'One income supporting the mortgage — critical to protect against income loss.' },
];

/* ─── Component ─────────────────────────────────────────────────────────── */

export default function MortgageLifeInsuranceIndianapolisPage() {
  const [openFAQ, setOpenFAQ] = useState<number | null>(null);
  const [showBookingModal, setShowBookingModal] = useState(false);

  // Calculator state
  const [loanBalance, setLoanBalance] = useState(280000);
  const [termYears, setTermYears] = useState(20);
  const [age, setAge] = useState(40);
  const [healthClass, setHealthClass] = useState<'preferred' | 'standard' | 'substandard'>('standard');
  const [calcResult, setCalcResult] = useState<PayoffResult | null>(null);

  useEffect(() => {
    window.scrollTo(0, 0);

    const title = 'Mortgage Life Insurance Indianapolis IN | Protect Your Home | Hoosier Life Insurance';
    const description = 'Mortgage life insurance in Indianapolis, IN — protect your home and family. Compare mortgage protection vs. term life. Use our Mortgage Payoff Calculator. Call (317) 648-2983.';
    const keywords = 'mortgage life insurance Indianapolis, mortgage protection insurance Indianapolis, mortgage payoff insurance Indianapolis Indiana, home loan life insurance Indianapolis';

    document.title = title;

    const metaTags = [
      { name: 'description', content: description },
      { name: 'keywords', content: keywords },
      { name: 'last-modified', content: new Date().toISOString() },
      { property: 'og:title', content: title },
      { property: 'og:description', content: description },
      { property: 'og:url', content: CANONICAL },
      { property: 'og:type', content: 'website' },
      { property: 'og:image', content: 'https://public.readdy.ai/ai/img_res/defde824d25dcb69cdccc689cc854a07.jpg' },
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'twitter:title', content: title },
      { name: 'twitter:description', content: description },
      { name: 'geo.position', content: '39.7684;-86.1581' },
      { name: 'geo.placename', content: 'Indianapolis, Indiana' },
      { name: 'geo.region', content: 'US-IN' },
      { name: 'ICBM', content: '39.7684, -86.1581' },
    ];

    const addedMetas: HTMLMetaElement[] = [];
    metaTags.forEach(tag => {
      const meta = document.createElement('meta');
      if (tag.name) meta.setAttribute('name', tag.name);
      if (tag.property) meta.setAttribute('property', tag.property);
      meta.setAttribute('content', tag.content);
      document.head.appendChild(meta);
      addedMetas.push(meta);
    });

    const linkCanonical = document.createElement('link');
    linkCanonical.rel = 'canonical';
    linkCanonical.href = CANONICAL;
    document.head.appendChild(linkCanonical);

    const schemas = [
      {
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        name: title,
        description,
        url: CANONICAL,
        inLanguage: 'en-US',
        dateModified: '2026-04-12T00:00:00Z',
        isPartOf: { '@type': 'WebSite', name: 'Hoosier Life Insurance', url: SITE_URL },
        about: [
          { '@type': 'Thing', name: 'Mortgage Life Insurance Indianapolis' },
          { '@type': 'Thing', name: 'Mortgage Protection Insurance Indianapolis' },
          { '@type': 'Thing', name: 'Home Loan Life Insurance Indianapolis Indiana' },
        ],
      },
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
          { '@type': 'ListItem', position: 2, name: 'Life Insurance Indianapolis', item: `${SITE_URL}/life-insurance-indianapolis` },
          { '@type': 'ListItem', position: 3, name: 'Mortgage Life Insurance Indianapolis', item: CANONICAL },
        ],
      },
      {
        '@context': 'https://schema.org',
        '@type': 'Service',
        serviceType: 'Mortgage Life Insurance',
        name: 'Mortgage Life Insurance Indianapolis',
        description: 'Mortgage life insurance and mortgage protection coverage for Indianapolis and Marion County homeowners. Compare mortgage life vs. term life — independent agent, 20+ carriers compared.',
        url: CANONICAL,
        provider: {
          '@type': 'InsuranceAgency',
          name: 'Hoosier Life Insurance',
          telephone: '+1-317-648-2983',
          url: SITE_URL,
          address: {
            '@type': 'PostalAddress',
            streetAddress: '5625 German Church Rd',
            addressLocality: 'Indianapolis',
            addressRegion: 'IN',
            postalCode: '46235',
            addressCountry: 'US',
          },
          geo: { '@type': 'GeoCoordinates', latitude: 39.7684, longitude: -86.1581 },
          openingHoursSpecification: [
            { '@type': 'OpeningHoursSpecification', dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'], opens: '09:00', closes: '17:00' },
            { '@type': 'OpeningHoursSpecification', dayOfWeek: ['Saturday'], opens: '08:00', closes: '13:00' },
          ],
        },
        areaServed: [
          { '@type': 'City', name: 'Indianapolis', containedIn: { '@type': 'AdministrativeArea', name: 'Marion County' } },
          { '@type': 'City', name: 'Carmel' },
          { '@type': 'City', name: 'Fishers' },
          { '@type': 'City', name: 'Noblesville' },
          { '@type': 'State', name: 'Indiana' },
        ],
        aggregateRating: {
          '@type': 'AggregateRating',
          ratingValue: '4.9',
          reviewCount: '127',
          bestRating: '5',
        },
      },
      {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: FAQS.map(faq => ({
          '@type': 'Question',
          name: faq.q,
          acceptedAnswer: { '@type': 'Answer', text: faq.a },
        })),
      },
    ];

    const scriptTag = document.createElement('script');
    scriptTag.type = 'application/ld+json';
    scriptTag.text = JSON.stringify(schemas);
    document.head.appendChild(scriptTag);

    return () => {
      addedMetas.forEach(m => { if (document.head.contains(m)) document.head.removeChild(m); });
      if (document.head.contains(linkCanonical)) document.head.removeChild(linkCanonical);
      if (document.head.contains(scriptTag)) document.head.removeChild(scriptTag);
    };
  }, []);

  const handleCalculate = () => {
    const result = calculatePayoff(loanBalance, termYears, age, healthClass);
    setCalcResult(result);
  };

  const balanceOptions = [150000, 200000, 250000, 280000, 300000, 350000, 400000, 500000];
  const termOptions = [10, 15, 20, 25, 30];
  const ageOptions = [25, 30, 35, 40, 45, 50, 55, 60];

  return (
    <div className="min-h-screen bg-[#F9F7F4] font-['Inter',sans-serif]">
      <Header />
      <ProductNav />

      {/* Breadcrumb */}
      <nav aria-label="Breadcrumb" className="bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-6 py-3 flex items-center gap-2 text-sm">
          <Link to="/" className="text-gray-500 hover:text-[#D97642] transition-colors flex items-center gap-1 whitespace-nowrap">
            <i className="ri-home-4-line text-sm"></i> Home
          </Link>
          <i className="ri-arrow-right-s-line text-gray-300"></i>
          <Link to="/life-insurance-indianapolis" className="text-gray-500 hover:text-[#D97642] transition-colors whitespace-nowrap">
            Life Insurance Indianapolis
          </Link>
          <i className="ri-arrow-right-s-line text-gray-300"></i>
          <span className="text-[#1A3A52] font-semibold whitespace-nowrap">Mortgage Life Insurance</span>
        </div>
      </nav>

      {/* ── HERO ─────────────────────────────────────────────────────────── */}
      <section className="relative overflow-hidden min-h-[580px]">
        <div className="absolute inset-0 w-full h-full">
          <img
            src="https://readdy.ai/api/search-image?query=Indianapolis%20Indiana%20suburban%20neighborhood%20aerial%20view%20beautiful%20homes%20with%20green%20lawns%20warm%20golden%20hour%20light%20Marion%20County%20Indiana%20residential%20streets%20safe%20family%20neighborhood%20mortgage%20protection%20life%20insurance%20concept%20photorealistic%20warm%20tones%20peaceful%20community&width=1440&height=700&seq=mortgagelife-indy-hero-2025a&orientation=landscape"
            alt="Mortgage Life Insurance Indianapolis Indiana — Protect Your Home and Family"
            title="Mortgage Life Insurance in Indianapolis, Indiana — Mortgage Payoff Calculator"
            loading="eager"
            fetchPriority="high"
            decoding="async"
            width={1440}
            height={700}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#1A3A52]/96 via-[#1A3A52]/85 to-[#1A3A52]/30"></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-6 py-20 md:py-28 w-full">
          <div className="max-w-[720px]">
            <span className="inline-flex items-center gap-2 bg-[#D97642]/20 border border-[#D97642]/40 text-[#F4A97A] text-xs font-semibold tracking-widest uppercase px-4 py-1.5 rounded-full mb-6">
              <i className="ri-home-4-line text-sm"></i>
              Indianapolis &amp; Marion County · Mortgage Payoff Protection
            </span>

            <h1 className="text-4xl sm:text-5xl md:text-6xl font-extrabold text-white leading-tight mb-6">
              Protect Your Home with Mortgage Life Insurance —{' '}
              <span className="text-[#D97642]">Indianapolis, IN</span>
            </h1>

            <p className="text-lg md:text-xl text-white/90 leading-relaxed mb-6 max-w-[640px]">
              Protect your Indianapolis home. If you die, your family keeps the house — mortgage paid in full. Use our <strong className="text-white">Mortgage Payoff Calculator</strong> to compare mortgage life insurance vs. level term and find your best option.
            </p>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-8">
              {[
                { value: '$0', label: 'Remaining mortgage if you die' },
                { value: '20+', label: 'Carriers compared for you' },
                { value: '24 hrs', label: 'No-exam approval available' },
                { value: 'Free', label: 'Mortgage payoff consultation' },
              ].map(item => (
                <div key={item.label} className="bg-white/15 backdrop-blur-sm border border-white/25 rounded-xl px-4 py-3 text-center">
                  <div className="text-xl font-extrabold text-[#D97642]">{item.value}</div>
                  <div className="text-xs text-white/80 mt-0.5">{item.label}</div>
                </div>
              ))}
            </div>

            <div className="flex flex-wrap gap-3">
              <a
                href="#calculator"
                className="inline-flex items-center gap-2 px-7 py-4 bg-[#D97642] text-white text-base font-bold rounded-xl hover:bg-[#C26638] transition-all cursor-pointer whitespace-nowrap"
              >
                <i className="ri-calculator-line text-lg"></i>
                Use Mortgage Payoff Calculator
              </a>
              <a
                href="tel:+13176482983"
                className="inline-flex items-center gap-2 px-7 py-4 bg-white/20 border border-white/40 text-white text-base font-semibold rounded-xl hover:bg-white/30 transition-all cursor-pointer whitespace-nowrap"
              >
                <i className="ri-phone-fill text-lg"></i>
                (317) 648-2983
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Bar */}
      <section className="bg-[#1A3A52] py-8">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 text-center">
            {[
              { value: '$285K', label: 'Average Indianapolis home mortgage balance (2026)' },
              { value: '30 yrs', label: 'Typical mortgage term — your family needs protection the whole time' },
              { value: '40%', label: 'Savings vs. mortgage life insurance when you choose level term' },
              { value: '20+', label: 'Carriers compared — we find your best mortgage protection rate' },
            ].map(stat => (
              <div key={stat.label} className="flex flex-col items-center gap-1">
                <span className="text-2xl md:text-3xl font-extrabold text-[#D97642]">{stat.value}</span>
                <span className="text-xs text-white/70 leading-snug max-w-[160px]">{stat.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Key Takeaways */}
      <section className="py-8 bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-start gap-4">
            <div className="w-8 h-8 flex items-center justify-center flex-shrink-0 mt-0.5">
              <i className="ri-home-heart-line text-[#D97642] text-2xl"></i>
            </div>
            <div className="flex-1">
              <h2 className="text-[#1A3A52] font-bold text-base mb-3">Key Facts: Mortgage Life Insurance in Indianapolis</h2>
              <ul className="grid md:grid-cols-2 gap-x-8 gap-y-2">
                {[
                  'Mortgage life insurance pays off your remaining mortgage balance if you die — your family keeps the home',
                  'Most Indianapolis homeowners save 20–40% by choosing level term life insurance instead of mortgage life insurance',
                  'Level term pays your family directly — they can pay the mortgage, cover living expenses, or invest the money',
                  'No-exam mortgage protection available — simplified issue approved in 24–48 hours, up to $500K coverage',
                  'Mortgage life insurance death benefit decreases as you pay down your loan — but your premium stays the same',
                  'An independent agent compares all available carriers simultaneously — no obligation, no pressure',
                ].map((bullet, i) => (
                  <li key={i} className="flex items-start gap-2 text-gray-600 text-sm">
                    <i className="ri-arrow-right-s-line text-[#D97642] flex-shrink-0 mt-0.5"></i>
                    <span>{bullet}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-3 text-gray-400 text-xs">Last reviewed April 2026 · Licensed Life Insurance Specialists at Hoosier Life Insurance · Indiana Licensed · Independent — 20+ Carriers</div>
            </div>
          </div>
        </div>
      </section>

      {/* ── MAIN CONTENT + SIDEBAR ───────────────────────────────────────── */}
      <section className="max-w-7xl mx-auto px-6 py-14 md:py-20">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_380px] gap-12 xl:gap-16 items-start">

          {/* LEFT COLUMN */}
          <div>

            {/* ── MORTGAGE PAYOFF CALCULATOR ────────────────────────────── */}
            <div id="calculator" className="mb-16 scroll-mt-24">
              <span className="text-xs font-semibold tracking-widest text-[#D97642] uppercase mb-3 block">Interactive Tool</span>
              <h2 className="text-2xl md:text-3xl font-bold text-[#1A3A52] mb-3 leading-tight">
                Mortgage Payoff Calculator — Indianapolis
              </h2>
              <p className="text-sm text-gray-500 mb-8">
                Enter your mortgage details to compare mortgage life insurance vs. level term life insurance — see exact monthly costs, total savings, and our recommendation for your situation.
              </p>

              <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
                <div className="bg-[#1A3A52] px-7 py-7">
                  <h3 className="text-base font-bold text-white mb-6 flex items-center gap-2">
                    <i className="ri-home-4-line text-[#D97642]"></i>
                    Your Mortgage Details
                  </h3>

                  {/* Loan Balance */}
                  <div className="mb-6">
                    <label className="text-xs font-semibold text-white/70 uppercase tracking-wide mb-3 block">
                      Current Mortgage Balance: <span className="text-[#D97642] font-extrabold">${loanBalance.toLocaleString()}</span>
                    </label>
                    <div className="flex flex-wrap gap-2">
                      {balanceOptions.map(b => (
                        <button
                          key={b}
                          onClick={() => { setLoanBalance(b); setCalcResult(null); }}
                          className={`px-3 py-2 rounded-lg text-sm font-semibold transition-all cursor-pointer whitespace-nowrap ${loanBalance === b ? 'bg-[#D97642] text-white' : 'bg-white/10 text-white/80 hover:bg-white/20'}`}
                        >
                          ${(b / 1000).toFixed(0)}K
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Term */}
                  <div className="mb-6">
                    <label className="text-xs font-semibold text-white/70 uppercase tracking-wide mb-3 block">
                      Remaining Term: <span className="text-[#D97642] font-extrabold">{termYears} years</span>
                    </label>
                    <div className="flex flex-wrap gap-2">
                      {termOptions.map(t => (
                        <button
                          key={t}
                          onClick={() => { setTermYears(t); setCalcResult(null); }}
                          className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all cursor-pointer whitespace-nowrap ${termYears === t ? 'bg-[#D97642] text-white' : 'bg-white/10 text-white/80 hover:bg-white/20'}`}
                        >
                          {t} yr
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Age */}
                  <div className="mb-6">
                    <label className="text-xs font-semibold text-white/70 uppercase tracking-wide mb-3 block">
                      Your Age: <span className="text-[#D97642] font-extrabold">{age}</span>
                    </label>
                    <div className="flex flex-wrap gap-2">
                      {ageOptions.map(a => (
                        <button
                          key={a}
                          onClick={() => { setAge(a); setCalcResult(null); }}
                          className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all cursor-pointer whitespace-nowrap ${age === a ? 'bg-[#D97642] text-white' : 'bg-white/10 text-white/80 hover:bg-white/20'}`}
                        >
                          {a}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Health Class */}
                  <div className="mb-7">
                    <label className="text-xs font-semibold text-white/70 uppercase tracking-wide mb-3 block">Health Class</label>
                    <div className="grid grid-cols-3 gap-2">
                      {([
                        { value: 'preferred', label: 'Preferred', desc: 'Excellent health' },
                        { value: 'standard', label: 'Standard', desc: 'Good health' },
                        { value: 'substandard', label: 'Rated', desc: 'Health conditions' },
                      ] as const).map(opt => (
                        <button
                          key={opt.value}
                          onClick={() => { setHealthClass(opt.value); setCalcResult(null); }}
                          className={`px-4 py-3 rounded-lg text-left transition-all cursor-pointer ${healthClass === opt.value ? 'bg-[#D97642] text-white' : 'bg-white/10 text-white/80 hover:bg-white/20'}`}
                        >
                          <div className="text-sm font-bold">{opt.label}</div>
                          <div className={`text-xs mt-0.5 ${healthClass === opt.value ? 'text-white/80' : 'text-white/50'}`}>{opt.desc}</div>
                        </button>
                      ))}
                    </div>
                  </div>

                  <button
                    onClick={handleCalculate}
                    className="w-full flex items-center justify-center gap-3 px-6 py-4 bg-[#D97642] text-white rounded-xl hover:bg-[#C26638] transition-all cursor-pointer font-bold text-base"
                  >
                    <i className="ri-calculator-line text-lg"></i>
                    Calculate My Mortgage Protection Cost
                  </button>
                </div>

                {/* Results */}
                {calcResult !== null && (
                  <div className="px-7 py-7">
                    <h3 className="text-base font-bold text-[#1A3A52] mb-6">Your Mortgage Protection Results</h3>

                    {/* Cost Comparison */}
                    <div className="grid grid-cols-2 gap-4 mb-6">
                      <div className="bg-gray-50 rounded-xl p-5 border border-gray-200">
                        <div className="text-xs text-gray-400 uppercase tracking-wide mb-2 font-semibold">Mortgage Life Insurance</div>
                        <div className="text-3xl font-extrabold text-gray-500 mb-1">${calcResult.mortgageLifeMonthly.toFixed(0)}<span className="text-base font-normal">/mo</span></div>
                        <div className="text-xs text-gray-400">Decreasing benefit · Lender is beneficiary</div>
                      </div>
                      <div className="bg-[#4A7C59]/5 rounded-xl p-5 border-2 border-[#4A7C59]/30">
                        <div className="text-xs text-[#4A7C59] uppercase tracking-wide mb-2 font-semibold flex items-center gap-1">
                          <i className="ri-star-fill text-xs"></i>
                          Level Term Life Insurance
                        </div>
                        <div className="text-3xl font-extrabold text-[#4A7C59] mb-1">${calcResult.termLifeMonthly.toFixed(0)}<span className="text-base font-normal">/mo</span></div>
                        <div className="text-xs text-[#4A7C59]/70">Level benefit · Your family is beneficiary</div>
                      </div>
                    </div>

                    {/* Savings Banner */}
                    <div className="bg-[#D97642]/10 border border-[#D97642]/30 rounded-xl p-5 mb-6">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#D97642] flex-shrink-0">
                          <i className="ri-money-dollar-circle-line text-white text-xl"></i>
                        </div>
                        <div>
                          <div className="text-sm font-bold text-[#1A3A52]">
                            You save approximately <span className="text-[#D97642]">${Math.round(calcResult.termLifeSavings).toLocaleString()}</span> over {calcResult.termYears} years by choosing level term
                          </div>
                          <div className="text-xs text-gray-500 mt-0.5">Plus: your family receives the full ${calcResult.loanBalance.toLocaleString()} regardless of remaining mortgage balance</div>
                        </div>
                      </div>
                    </div>

                    {/* Recommendation */}
                    <div className="bg-[#1A3A52]/5 rounded-xl p-5 mb-6">
                      <div className="flex items-start gap-3">
                        <div className="w-8 h-8 flex items-center justify-center rounded-full bg-[#1A3A52] flex-shrink-0 mt-0.5">
                          <i className="ri-lightbulb-line text-white text-sm"></i>
                        </div>
                        <div>
                          <div className="text-sm font-bold text-[#1A3A52] mb-1">Our Recommendation: {calcResult.recommendation}</div>
                          <p className="text-xs text-gray-600 leading-relaxed">{calcResult.recommendationDetail}</p>
                        </div>
                      </div>
                    </div>

                    <div className="bg-gray-50 rounded-xl p-4 mb-5">
                      <p className="text-xs text-gray-400 leading-relaxed">
                        <strong className="text-[#1A3A52]">Disclaimer:</strong> Estimates based on general rate tables for Indianapolis, IN. Actual rates depend on full underwriting, carrier selection, and your specific health profile. Call <a href="tel:+13176482983" className="text-[#D97642] font-semibold">(317) 648-2983</a> for your exact personalized quote.
                      </p>
                    </div>

                    <button
                      onClick={() => setShowBookingModal(true)}
                      className="w-full flex items-center justify-center gap-2 px-6 py-4 bg-[#D97642] text-white rounded-xl hover:bg-[#C26638] transition-all cursor-pointer font-bold"
                    >
                      <i className="ri-calendar-check-line text-lg"></i>
                      Get My Exact Mortgage Protection Quote — Free
                    </button>
                  </div>
                )}

                {calcResult === null && (
                  <div className="px-7 py-10 text-center">
                    <div className="w-16 h-16 flex items-center justify-center rounded-full bg-[#D97642]/10 mx-auto mb-4">
                      <i className="ri-calculator-line text-3xl text-[#D97642]"></i>
                    </div>
                    <p className="text-sm text-gray-500">Select your mortgage balance, term, age, and health class above, then click <strong className="text-[#1A3A52]">Calculate My Mortgage Protection Cost</strong>.</p>
                  </div>
                )}
              </div>
            </div>

            {/* Mortgage Life vs Term Life Comparison */}
            <div className="mb-14">
              <span className="text-xs font-semibold tracking-widest text-[#D97642] uppercase mb-3 block">Side-by-Side Comparison</span>
              <h2 className="text-2xl md:text-3xl font-bold text-[#1A3A52] mb-4 leading-tight">
                Mortgage Life Insurance vs. Term Life Insurance — Indianapolis
              </h2>
              <p className="text-base text-gray-600 leading-relaxed mb-6">
                Most Indianapolis homeowners are better served by level term life insurance than traditional mortgage life insurance. Here's exactly why:
              </p>

              <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
                <div className="bg-[#1A3A52] px-6 py-4 grid grid-cols-3 gap-4">
                  <div className="text-xs font-bold text-white/60 uppercase tracking-wide">Feature</div>
                  <div className="text-xs font-bold text-white/80 uppercase tracking-wide text-center">Mortgage Life Insurance</div>
                  <div className="text-xs font-bold text-[#D97642] uppercase tracking-wide text-center flex items-center justify-center gap-1">
                    <i className="ri-star-fill text-xs"></i>
                    Level Term Life
                  </div>
                </div>
                <div className="divide-y divide-gray-100">
                  {COMPARISON.map((row, i) => (
                    <div key={i} className={`grid grid-cols-3 gap-4 px-6 py-4 ${i % 2 === 0 ? 'bg-white' : 'bg-[#F9F7F4]'}`}>
                      <div className="text-sm font-semibold text-[#1A3A52]">{row.feature}</div>
                      <div className="text-sm text-gray-500 text-center flex items-center justify-center">
                        {row.winner === 'term' ? (
                          <span className="flex items-center gap-1 text-red-400">
                            <i className="ri-close-circle-line text-sm"></i>
                            <span className="text-xs">{row.mortgageLife}</span>
                          </span>
                        ) : (
                          <span className="text-xs text-gray-500">{row.mortgageLife}</span>
                        )}
                      </div>
                      <div className="text-sm text-center flex items-center justify-center">
                        {row.winner === 'term' ? (
                          <span className="flex items-center gap-1 text-[#4A7C59]">
                            <i className="ri-checkbox-circle-line text-sm"></i>
                            <span className="text-xs font-semibold">{row.termLife}</span>
                          </span>
                        ) : (
                          <span className="text-xs text-gray-500">{row.termLife}</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
                <div className="px-6 py-4 bg-[#4A7C59]/5 border-t border-[#4A7C59]/20">
                  <p className="text-xs text-[#4A7C59] font-semibold flex items-center gap-2">
                    <i className="ri-information-line"></i>
                    For most Indianapolis homeowners, level term life insurance is the better choice — lower cost, more flexibility, and your family is always the beneficiary.
                  </p>
                </div>
              </div>
            </div>

            {/* Who Needs Mortgage Life */}
            <div className="mb-14">
              <span className="text-xs font-semibold tracking-widest text-[#D97642] uppercase mb-3 block">Is This Right for You?</span>
              <h2 className="text-2xl md:text-3xl font-bold text-[#1A3A52] mb-4 leading-tight">
                Who Needs Mortgage Life Insurance in Indianapolis?
              </h2>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {WHO_NEEDS.map((item, i) => (
                  <div key={i} className="bg-white rounded-xl p-5 border border-gray-100">
                    <div className="w-11 h-11 flex items-center justify-center rounded-xl bg-[#D97642]/10 mb-4">
                      <i className={`${item.icon} text-xl text-[#D97642]`}></i>
                    </div>
                    <h3 className="text-sm font-bold text-[#1A3A52] mb-2">{item.title}</h3>
                    <p className="text-xs text-gray-500 leading-relaxed">{item.desc}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Testimonials */}
            <div className="mb-14">
              <span className="text-xs font-semibold tracking-widest text-[#D97642] uppercase mb-3 block">Real Stories</span>
              <h2 className="text-2xl md:text-3xl font-bold text-[#1A3A52] mb-8 leading-tight">
                Indianapolis Homeowners Who Protected Their Mortgage
              </h2>
              <div className="grid sm:grid-cols-2 gap-5">
                {TESTIMONIALS.map(t => (
                  <div key={t.name} className="bg-white rounded-xl p-6 border border-gray-100">
                    <div className="flex items-center gap-2 mb-4 flex-wrap">
                      <span className="bg-[#D97642]/10 text-[#D97642] text-xs font-bold px-3 py-1 rounded-full whitespace-nowrap">{t.coverage}</span>
                      <span className="bg-[#4A7C59]/10 text-[#4A7C59] text-xs font-bold px-3 py-1 rounded-full whitespace-nowrap">{t.savings}</span>
                    </div>
                    <div className="flex items-center gap-1 mb-3">
                      {Array.from({ length: t.stars }).map((_, i) => (
                        <i key={i} className="ri-star-fill text-[#D97642] text-sm"></i>
                      ))}
                    </div>
                    <p className="text-sm text-gray-600 leading-relaxed mb-4 italic">"{t.quote}"</p>
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 flex items-center justify-center rounded-full bg-[#1A3A52]/10">
                        <i className="ri-user-fill text-sm text-[#1A3A52]"></i>
                      </div>
                      <div>
                        <p className="text-sm font-bold text-[#1A3A52]">{t.name}</p>
                        <p className="text-xs text-gray-400">{t.location}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* FAQ */}
            <div id="faq">
              <span className="text-xs font-semibold tracking-widest text-[#D97642] uppercase mb-3 block">FAQ</span>
              <h2 className="text-2xl md:text-3xl font-bold text-[#1A3A52] mb-8 leading-tight">
                Mortgage Life Insurance Indianapolis — Frequently Asked Questions
              </h2>
              <div className="flex flex-col gap-3">
                {FAQS.map((faq, i) => (
                  <div key={i} className="bg-white rounded-xl border border-gray-100 overflow-hidden">
                    <button
                      onClick={() => setOpenFAQ(openFAQ === i ? null : i)}
                      className="w-full flex items-center justify-between px-6 py-5 text-left cursor-pointer hover:bg-gray-50 transition-colors"
                    >
                      <h3 className="text-sm font-bold text-[#1A3A52] pr-4 leading-snug">
                        <a id={`faq-mortgagelife-indy-${i}`}>{faq.q}</a>
                      </h3>
                      <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
                        <i className={`${openFAQ === i ? 'ri-subtract-line' : 'ri-add-line'} text-lg text-[#D97642]`}></i>
                      </div>
                    </button>
                    {openFAQ === i && (
                      <div className="px-6 pb-5">
                        <p className="text-sm text-gray-600 leading-relaxed">{faq.a}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

          </div>

          {/* RIGHT COLUMN — Sticky Sidebar */}
          <div className="lg:sticky lg:top-24 flex flex-col gap-6">

            {/* Quote CTA */}
            <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
              <div className="bg-[#1A3A52] px-7 py-6">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#D97642]/20">
                    <i className="ri-home-heart-fill text-xl text-[#D97642]"></i>
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-white leading-tight">Free Mortgage Protection Quote</h3>
                    <p className="text-xs text-white/70">Compare all carriers · No obligation</p>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2 mt-4">
                  <span className="bg-white/10 text-white/90 text-xs font-medium px-3 py-1 rounded-full whitespace-nowrap">✓ 20+ carriers compared</span>
                  <span className="bg-white/10 text-white/90 text-xs font-medium px-3 py-1 rounded-full whitespace-nowrap">✓ No-exam options</span>
                  <span className="bg-white/10 text-white/90 text-xs font-medium px-3 py-1 rounded-full whitespace-nowrap">✓ Free consultation</span>
                </div>
              </div>
              <div className="px-7 py-7">
                <div className="flex flex-col gap-3">
                  <button
                    onClick={() => setShowBookingModal(true)}
                    className="w-full flex items-center justify-center gap-3 px-6 py-4 bg-[#D97642] text-white rounded-xl hover:bg-[#C26638] transition-all cursor-pointer font-bold"
                  >
                    <i className="ri-calendar-check-line text-lg"></i>
                    Book Free Consultation
                  </button>
                  <a
                    href="tel:+13176482983"
                    className="w-full flex items-center justify-center gap-3 px-6 py-4 bg-[#1A3A52] text-white rounded-xl hover:bg-[#0F2A3F] transition-all cursor-pointer font-bold"
                  >
                    <i className="ri-phone-fill text-lg"></i>
                    Call (317) 648-2983
                  </a>
                  <Link
                    to="/instant-quote"
                    className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-gray-100 text-[#1A3A52] rounded-xl hover:bg-gray-200 transition-all cursor-pointer font-semibold text-sm"
                  >
                    <i className="ri-file-list-3-line"></i>
                    Get Instant Online Quote
                  </Link>
                </div>
                <p className="text-xs text-gray-400 text-center mt-4">Free · No obligation · Independent agent</p>
              </div>
            </div>

            {/* Quick Rate Reference */}
            <div className="bg-white rounded-2xl border border-gray-100 p-6">
              <h3 className="text-sm font-bold text-[#1A3A52] mb-4 flex items-center gap-2">
                <i className="ri-price-tag-3-line text-[#D97642]"></i>
                Sample Rates — $300K 20-Year Term (Indianapolis)
              </h3>
              <div className="flex flex-col gap-3">
                {[
                  { age: 'Age 30', preferred: '$18/mo', standard: '$24/mo' },
                  { age: 'Age 35', preferred: '$22/mo', standard: '$30/mo' },
                  { age: 'Age 40', preferred: '$30/mo', standard: '$42/mo' },
                  { age: 'Age 45', preferred: '$48/mo', standard: '$66/mo' },
                  { age: 'Age 50', preferred: '$78/mo', standard: '$108/mo' },
                ].map((row, i) => (
                  <div key={i} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
                    <span className="text-xs font-semibold text-[#1A3A52]">{row.age}</span>
                    <div className="flex gap-4">
                      <div className="text-right">
                        <div className="text-xs text-gray-400">Preferred</div>
                        <div className="text-xs font-bold text-[#4A7C59]">{row.preferred}</div>
                      </div>
                      <div className="text-right">
                        <div className="text-xs text-gray-400">Standard</div>
                        <div className="text-xs font-bold text-[#D97642]">{row.standard}</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <p className="text-xs text-gray-400 mt-3">Sample rates for illustration. Actual rates vary by carrier and health profile.</p>
            </div>

            {/* Booking Calendar */}
            <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
              <div className="bg-[#1A3A52] px-7 py-5">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 flex items-center justify-center rounded-full bg-[#D97642]/20">
                    <i className="ri-calendar-check-fill text-lg text-[#D97642]"></i>
                  </div>
                  <div>
                    <h3 className="text-base font-bold text-white leading-tight">Free 15-Min Mortgage Protection Call</h3>
                    <p className="text-xs text-white/70">Compare all options · No pressure</p>
                  </div>
                </div>
              </div>
              <div className="px-7 py-6">
                <BookingCalendar
                  timeslotsApi="https://readdy.ai/api/public/calendar/timeslots/9395c896-070b-4356-87d5-6717161b747b.670457f5037855118780bb2c7e1f4bd822f6755285fc48fb9798eacba5906de1"
                  appointmentsApi="https://readdy.ai/api/public/calendar/appointments/9395c896-070b-4356-87d5-6717161b747b.670457f5037855118780bb2c7e1f4bd822f6755285fc48fb9798eacba5906de1"
                  texts={{
                    heading: 'Book Your Mortgage Protection Call',
                    step0Title: 'Pick a Date',
                    step1Title: 'Choose a Time',
                    step2Title: 'Confirm Your Spot',
                    selectDate: 'Select Date',
                    selectTime: 'See Available Times →',
                    reviewAndBook: 'Confirm Booking',
                    previousMonth: 'Prev',
                    nextMonth: 'Next',
                    noSlotsAvailable: 'No slots available for this date',
                    selectedDateTime: 'Your Appointment',
                    modifyTime: 'Change',
                    customerName: 'Full Name',
                    customerPhone: 'Phone Number',
                    notes: "Mortgage balance and any questions? (Optional)",
                    submitButton: 'Confirm My Free Mortgage Protection Call',
                    submitting: 'Booking...',
                    successMessage: "You're booked! A confirmation text is on its way.",
                    errorMessage: 'Booking failed. Please try again.',
                    backButton: '← Back to Calendar',
                    required: '*',
                  }}
                  containerStyle=""
                />
              </div>
            </div>

            {/* Related Links */}
            <div className="bg-white rounded-xl border border-gray-100 p-6">
              <h3 className="text-sm font-bold text-[#1A3A52] mb-4">Related Pages</h3>
              <div className="flex flex-col gap-2">
                {[
                  { to: '/mortgage-protection-insurance-indianapolis', label: 'Mortgage Protection Insurance Indianapolis', icon: 'ri-shield-check-line' },
                  { to: '/term-life-insurance-indianapolis', label: 'Term Life Insurance Indianapolis', icon: 'ri-calendar-check-line' },
                  { to: '/life-insurance-quotes-indianapolis', label: 'Compare All Carriers — Indianapolis', icon: 'ri-search-line' },
                  { to: '/no-exam-life-insurance-indianapolis', label: 'No Medical Exam Life Insurance', icon: 'ri-timer-flash-line' },
                  { to: '/affordable-life-insurance-indianapolis', label: 'Affordable Life Insurance Indianapolis', icon: 'ri-money-dollar-circle-line' },
                ].map(link => (
                  <Link
                    key={link.to}
                    to={link.to}
                    className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-[#F9F7F4] transition-colors group"
                  >
                    <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-[#D97642]/10 flex-shrink-0">
                      <i className={`${link.icon} text-sm text-[#D97642]`}></i>
                    </div>
                    <span className="text-sm text-gray-600 group-hover:text-[#1A3A52] font-medium">{link.label}</span>
                    <i className="ri-arrow-right-s-line text-gray-300 ml-auto group-hover:text-[#D97642]"></i>
                  </Link>
                ))}
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Meet Your Agent */}
      <section className="py-16 bg-white border-t border-gray-100">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="relative">
              <img
                src="https://readdy.ai/api/search-image?query=Professional%20male%20insurance%20agent%20in%20his%20late%2030s%20wearing%20a%20charcoal%20blazer%20and%20white%20shirt%20friendly%20confident%20smile%20sitting%20at%20modern%20desk%20with%20laptop%20showing%20mortgage%20protection%20life%20insurance%20documents%20Indianapolis%20Indiana%20office%20natural%20window%20light%20trustworthy%20approachable%20financial%20advisor%20Marion%20County%20Indiana%20professional%20portrait%20photography%20warm%20tones&width=600&height=700&seq=agent-bwright-mortgagelife-indy-2025a&orientation=portrait"
                alt="Benjamin Wright — Mortgage Life Insurance Agent in Indianapolis, Indiana"
                title="Benjamin Wright — Mortgage Protection Life Insurance Specialist in Indianapolis"
                loading="lazy"
                decoding="async"
                width={600}
                height={700}
                className="rounded-2xl w-full h-[480px] object-cover object-top"
              />
              <div className="absolute bottom-6 right-6 bg-white rounded-xl px-5 py-4">
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 bg-[#D97642] rounded-full flex items-center justify-center">
                    <i className="ri-home-heart-line text-white text-xl"></i>
                  </div>
                  <div>
                    <div className="text-xs text-gray-500">Protecting</div>
                    <div className="text-base font-bold text-[#1A3A52]">Indianapolis Homes</div>
                  </div>
                </div>
              </div>
            </div>
            <div>
              <div className="inline-flex items-center gap-2 bg-[#D97642]/10 text-[#D97642] px-4 py-2 rounded-full text-sm font-medium mb-6">
                <i className="ri-user-heart-line"></i>
                Your Local Indianapolis Mortgage Protection Specialist
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-[#1A3A52] mb-6">Meet Benjamin Wright</h2>
              <p className="text-base text-gray-600 mb-5 leading-relaxed">
                I've helped hundreds of Indianapolis and Marion County homeowners protect their mortgages — and I've saved many of them thousands of dollars by showing them that level term life insurance is almost always a better deal than traditional mortgage life insurance.
              </p>
              <p className="text-base text-gray-600 mb-8 leading-relaxed">
                As an independent agent, I compare all available carriers simultaneously — no bias, no quotas. I'll show you the exact cost comparison for your mortgage balance, age, and health, and help you make the right decision for your family.
              </p>
              <div className="flex flex-wrap gap-4">
                <button
                  onClick={() => setShowBookingModal(true)}
                  className="px-7 py-4 bg-[#D97642] text-white rounded-lg font-semibold hover:bg-[#C26638] transition-all whitespace-nowrap cursor-pointer"
                >
                  Get My Mortgage Protection Quote
                </button>
                <a
                  href="tel:+13176482983"
                  className="px-7 py-4 bg-white border-2 border-gray-200 text-[#1A3A52] rounded-lg font-semibold hover:border-[#D97642] hover:text-[#D97642] transition-all whitespace-nowrap cursor-pointer"
                >
                  Call (317) 648-2983
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Cross-Links */}
      <section className="py-14 bg-gray-50 border-t border-gray-100">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-10">
            <h2 className="text-2xl md:text-3xl font-bold text-[#1A3A52] mb-3">Explore All Coverage Options</h2>
            <p className="text-sm text-gray-500 max-w-xl mx-auto">Find the right life insurance for your Indianapolis home and family</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {[
              { to: '/mortgage-protection-insurance-indianapolis', icon: 'ri-shield-check-line', title: 'Mortgage Protection Insurance', desc: 'Full mortgage protection coverage — disability, critical illness, and death benefit options' },
              { to: '/term-life-insurance-indianapolis', icon: 'ri-calendar-check-line', title: 'Term Life Insurance Indianapolis', desc: 'Level term coverage — the most affordable way to protect your mortgage and income' },
              { to: '/no-exam-life-insurance-indianapolis', icon: 'ri-timer-flash-line', title: 'No Medical Exam Life Insurance', desc: 'Approved in 24–48 hours — no blood draw, no doctor visit, up to $1M coverage' },
              { to: '/life-insurance-quotes-indianapolis', icon: 'ri-search-line', title: 'Compare All Carriers', desc: 'Interactive 6-carrier rate comparison — see all options ranked lowest to highest' },
            ].map(link => (
              <Link
                key={link.to}
                to={link.to}
                className="bg-white hover:bg-[#1A3A52] hover:text-white border border-gray-200 rounded-xl p-6 transition-all group cursor-pointer"
              >
                <i className={`${link.icon} text-3xl text-[#D97642] group-hover:text-white mb-3 block`}></i>
                <h3 className="font-bold text-base mb-2 text-[#1A3A52] group-hover:text-white">{link.title}</h3>
                <p className="text-sm text-gray-500 group-hover:text-gray-200">{link.desc}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="bg-[#1A3A52] py-14">
        <div className="max-w-2xl mx-auto px-6 text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-3">
            Protect your Indianapolis home — starting today.
          </h2>
          <p className="text-base text-white/75 mb-7">
            Use our Mortgage Payoff Calculator to compare your options, then call for your exact personalized rate. 20+ A-rated carriers compared. No obligation, no pressure.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a
              href="#calculator"
              className="inline-flex items-center gap-2 px-8 py-4 bg-[#D97642] text-white text-base font-bold rounded-xl hover:bg-[#C26638] transition-all cursor-pointer whitespace-nowrap"
            >
              <i className="ri-calculator-line text-lg"></i>
              Use Mortgage Payoff Calculator
            </a>
            <a
              href="tel:+13176482983"
              className="inline-flex items-center gap-2 px-8 py-4 bg-white/15 border border-white/30 text-white text-base font-semibold rounded-xl hover:bg-white/25 transition-all cursor-pointer whitespace-nowrap"
            >
              <i className="ri-phone-fill text-lg"></i>
              (317) 648-2983
            </a>
          </div>
        </div>
      </section>

      <StarRatingDisplay rating={4.9} reviewCount={127} productName="Mortgage Life Insurance Indianapolis" />
      <Footer />
      <MobileProductCTABar productName="Mortgage Life Insurance" quoteHref="/instant-quote" />

      {/* Booking Modal */}
      {showBookingModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <h3 className="text-xl font-bold text-[#1A3A52]">Get Your Mortgage Life Insurance Quote — Indianapolis</h3>
              <button onClick={() => setShowBookingModal(false)} className="text-gray-400 hover:text-gray-600 transition-colors cursor-pointer">
                <i className="ri-close-line text-3xl"></i>
              </button>
            </div>
            <div className="p-6">
              <iframe
                src="https://api.leadconnectorhq.com/widget/bookings/hoosier-life-insurance"
                style={{ width: '100%', height: '600px', border: 'none' }}
                title="Book Appointment"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
